/**
 * CustomerInvitesPage (iter203d) — admin bulk-invites customers to
 * activate their portal login. Paste a list of emails, each is
 * validated against Persons_for_Customer, a fresh invite token is
 * created, and an "Activate your account" email is sent via Gmail API.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

const STATUS_BADGE = {
  pending:  { color: "bg-amber-50  text-amber-800  border-amber-300",  label: "Pending" },
  accepted: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Accepted" },
  expired:  { color: "bg-slate-50 text-slate-500 border-slate-200",   label: "Expired" },
};

const RESULT_BADGE = {
  sent:          { color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Sent" },
  queued_mock:   { color: "bg-amber-50   text-amber-800   border-amber-300",   label: "Mock (email disabled)" },
  email_failed:  { color: "bg-red-50     text-red-700     border-red-300",     label: "Email failed" },
  not_a_customer:{ color: "bg-red-50     text-red-700     border-red-300",     label: "Not a customer" },
  invalid_email: { color: "bg-red-50     text-red-700     border-red-300",     label: "Invalid email" },
  already_active:{ color: "bg-slate-50   text-slate-500   border-slate-200",   label: "Already active" },
};

function fmtTs(v) {
  if (!v) return "—";
  try {
    const d = new Date(v.endsWith?.("Z") ? v : `${v}Z`);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      month: "short", day: "2-digit", year: "numeric",
    }).format(d);
  } catch { return String(v); }
}

export default function CustomerInvitesPage({ user, onLogout, onLogoutAll }) {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [expiresDays, setExpiresDays] = useState(7);
  const [sending, setSending] = useState(false);
  const [lastResults, setLastResults] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customer-invites", { params: { limit: 500 } });
      setInvites(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invites");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const sendInvites = async () => {
    const emails = emailsRaw.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);
    if (emails.length === 0) {
      toast.error("Paste at least one email address");
      return;
    }
    if (emails.length > 200) {
      toast.error("Max 200 emails per batch");
      return;
    }
    setSending(true);
    setLastResults(null);
    try {
      const r = await api.post("/admin/customer-invites", {
        emails, expires_in_days: expiresDays, resend_existing: true,
      });
      setLastResults(r.data);
      toast.success(`${r.data.sent} sent · ${r.data.failed} failed · ${r.data.total} total`);
      setEmailsRaw("");
      await loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invites");
    } finally { setSending(false); }
  };

  const resendOne = async (email) => {
    try {
      const r = await api.post("/admin/customer-invites/resend", { email });
      const res = r.data.results?.[0];
      if (res && res.status === "sent") {
        toast.success(`Fresh invite sent to ${email}`);
      } else {
        toast.error(`${email}: ${res?.status || "failed"}`);
      }
      await loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white"
         data-testid="customer-invites-page">
      <AdminPageHeader
        icon={<EnvelopeSimple size={20} weight="fill" className="text-blue-700" />}
        title="Customer Invites"
        subtitle="Send activation links to customers so they can sign in to the portal"
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
        testId="customer-invites-back"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {/* Compose panel */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
            New batch
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
              Customer emails <span className="text-slate-400 font-normal normal-case">— one per line (or paste comma-separated)</span>
            </label>
            <textarea value={emailsRaw}
              onChange={(e) => setEmailsRaw(e.target.value)}
              rows={6}
              data-testid="customer-invites-textarea"
              placeholder={"alice@example.com\nbob@example.com\ncarol@example.com"}
              className="w-full text-xs font-mono rounded-sm border border-slate-200 p-2" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs text-slate-600 flex items-center gap-2">
              Expires in
              <select value={expiresDays} onChange={(e) => setExpiresDays(Number(e.target.value))}
                      data-testid="customer-invites-expires"
                      className="h-8 rounded-sm border border-slate-200 px-2 text-xs bg-white">
                {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"}</option>)}
              </select>
            </label>
            <button onClick={sendInvites} disabled={sending || !emailsRaw.trim()}
                    data-testid="customer-invites-send"
                    className="h-9 px-4 rounded-sm text-xs font-bold uppercase tracking-wider
                               bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center gap-1.5">
              <PaperPlaneTilt size={13} weight="bold" />
              {sending ? "Sending…" : "Send invites"}
            </button>
            <span className="text-[11px] text-slate-500">
              Emails are sent via the currently configured provider (see Admin Settings → Email).
            </span>
          </div>
        </div>

        {/* Batch result summary */}
        {lastResults && (
          <div className="bg-white border border-slate-200 rounded-sm p-3"
               data-testid="customer-invites-last-batch">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
              Last batch — {lastResults.sent} sent, {lastResults.failed} failed, {lastResults.total} total
            </div>
            <div className="max-h-56 overflow-y-auto text-xs">
              {(lastResults.results || []).map((r, i) => {
                const b = RESULT_BADGE[r.status] || { color: "bg-slate-50 text-slate-500 border-slate-200", label: r.status };
                return (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-b-0">
                    <span className="font-mono text-slate-700">{r.email}</span>
                    <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${b.color}`}>
                      {b.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Existing invites list */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500" data-testid="customer-invites-count">
            {loading ? "Loading…" : `${invites.length.toLocaleString()} invite(s) on record`}
          </div>
          <button onClick={loadInvites} disabled={loading}
                  data-testid="customer-invites-refresh"
                  className="h-8 px-3 rounded-sm border text-xs font-bold uppercase tracking-wider
                             bg-white text-slate-700 border-slate-200 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="customer-invites-table">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Company</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Sent</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Expires</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Sent by</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400"
                        data-testid="customer-invites-empty">
                  No invites yet — send the first batch above.
                </td></tr>
              )}
              {invites.map((it, idx) => {
                const b = STATUS_BADGE[it.status] || STATUS_BADGE.pending;
                return (
                  <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{it.email}</td>
                    <td className="px-3 py-2 hidden sm:table-cell text-slate-600">{it.company_name || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${b.color}`}>
                        {b.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-slate-500">{fmtTs(it.created_at)}</td>
                    <td className="px-3 py-2 hidden md:table-cell text-slate-500">{fmtTs(it.expires_at)}</td>
                    <td className="px-3 py-2 hidden lg:table-cell text-slate-500 font-mono text-[11px]">{it.created_by || "—"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {it.status !== "accepted" && (
                        <button onClick={() => resendOne(it.email)}
                                data-testid={`customer-invites-resend-${it.email}`}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                                           bg-white text-blue-700 border-blue-200 hover:bg-blue-50">
                          <PaperPlaneTilt size={11} weight="bold" /> Resend
                        </button>
                      )}
                      {it.status === "accepted" && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px]">
                          <CheckCircle size={12} weight="fill" /> Active
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
          <Warning size={12} weight="bold" className="text-amber-500 mt-0.5" />
          Invites are only issued to emails that already exist in
          <code className="mx-1 px-1 bg-slate-100 rounded-sm">Persons_for_Customer</code>.
          Add missing customers via the Customers page first.
        </div>
      </div>
    </div>
  );
}
